import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, EmailAuthProvider, reauthenticateWithCredential, deleteUser, onAuthStateChanged } from 'firebase/auth';
import LearningLayout from '../LearningPlatform/LearningLayout';
import { FaLock, FaUserSlash, FaExclamationTriangle } from 'react-icons/fa';

const DeleteAccount = () => {
    const [password, setPassword] = useState('');
    const [user, setUser] = useState(null);
    const [incorrectPassword, setIncorrectPassword] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const auth = getAuth();

        const unsubscribe = onAuthStateChanged(auth, currentUser => {
            setUser(currentUser);
        });

        return unsubscribe;
    }, []);

    const handleChange = (event) => {
        setPassword(event.target.value);
        setIncorrectPassword(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setIsDeleting(true);

        if (user) {
            const credential = EmailAuthProvider.credential(user.email, password);

            try {
                await reauthenticateWithCredential(user, credential);
                await deleteUser(user);
                setIncorrectPassword(false);
                navigate('/');
            } catch (error) {
                console.error('Error in account deletion: ', error);
                setIncorrectPassword(true);
                setPassword("");
                setIsDeleting(false);
            }
        }
    };

    return (
        <LearningLayout>
            <div className='flex-1 overflow-auto flex flex-col'>
                <div className='flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8'>
                    <div className='w-full max-w-md'>
                        {/* Form Card */}
                        <div className='bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-white/50 overflow-hidden'>
                            {/* Header */}
                            <div className='bg-gradient-to-r from-red-600 to-red-700 p-8 text-center'>
                                <div className='flex justify-center mb-4'>
                                    <div className='bg-white/20 backdrop-blur-sm p-4 rounded-full'>
                                        <FaUserSlash className='text-4xl text-white' />
                                    </div>
                                </div>
                                <h1 className='text-3xl font-bold text-white mb-2'>Deletar Conta</h1>
                                <p className='text-white/90 text-sm'>Esta ação não pode ser desfeita</p>
                            </div>

                            {/* Form */}
                            <form onSubmit={handleSubmit} className='p-8'>
                                {/* Warning Message */}
                                <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg'>
                                    <div className='flex items-start gap-3'>
                                        <FaExclamationTriangle className='text-red-600 mt-0.5 flex-shrink-0' />
                                        <div className='text-sm text-red-800'>
                                            <p className='font-semibold mb-1'>Atenção!</p>
                                            <p>Ao deletar sua conta, todos os seus dados de progresso serão permanentemente removidos. Esta ação não pode ser revertida.</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Error Message */}
                                {incorrectPassword && (
                                    <div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-lg'>
                                        <p className='text-red-600 text-sm'>Senha inválida! Tente novamente.</p>
                                    </div>
                                )}

                                {/* Password Input */}
                                <div className='mb-6'>
                                    <label className='block text-sm font-medium text-gray-700 mb-2'>
                                        Confirme sua senha para continuar
                                    </label>
                                    <div className='relative'>
                                        <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                                            <FaLock className='text-gray-400' />
                                        </div>
                                        <input
                                            type="password"
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={handleChange}
                                            required
                                            disabled={isDeleting}
                                            className='w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 placeholder-gray-400 disabled:bg-gray-100'
                                        />
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isDeleting}
                                    className='w-full bg-gradient-to-r from-red-600 to-red-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none'
                                >
                                    {isDeleting ? 'Deletando...' : 'Deletar Conta Permanentemente'}
                                </button>

                                {/* Cancel Link */}
                                <div className='mt-6 text-center'>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/progress')}
                                        className='text-gray-600 hover:text-gray-700 text-sm font-medium transition-colors'
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </LearningLayout>
    );
};

export default DeleteAccount;
